import { html, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { BaseElement, customElement } from '../../base-element';
import '../../text';
import '../../image';
import styles from './loading-info.scss?inline';

@customElement('ps-loading-info')
export class LoadingInfoWC extends BaseElement {
  static styles = unsafeCSS(styles);

  @property({ type: Array }) steps: string[] = [];

  @property({ type: Boolean, reflect: true }) paused = false;

  /** Icon shown in the loader tile. No default — this is a shared primitive, not tied to any one product. */
  @property() icon = '';

  /**
   * When true, the message strip wraps around to the first step instead of
   * stopping at the last one — for continuous contexts like an app-switch
   * transition. Defaults to false to preserve the original one-shot behavior
   * used by step-based progress flows (e.g. solution creation).
   */
  @property({ type: Boolean, reflect: true }) loop = false;

  private currentStepIndex = 0;

  private animationInterval: number | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.currentStepIndex = 0;
    if (!this.paused) this.startAnimation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopAnimation();
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    if (changedProperties.has('paused')) {
      if (!this.paused) {
        this.startAnimation();
      } else {
        this.stopAnimation();
      }
    }

    if (
      changedProperties.has('steps') &&
      this.currentStepIndex > Math.max(0, this.steps.length - 1)
    ) {
      this.currentStepIndex = Math.max(0, this.steps.length - 1);
    }
  }

  private startAnimation() {
    if (this.animationInterval !== null) {
      return;
    }

    // Animate through all steps with timing
    // Each step stays visible for 1.5s before transitioning to the next
    this.animationInterval = window.setInterval(() => {
      if (this.currentStepIndex < this.steps.length - 1) {
        this.currentStepIndex += 1;
        this.requestUpdate();
      } else if (this.loop) {
        this.currentStepIndex = 0;
        this.requestUpdate();
      } else {
        this.stopAnimation();
      }
    }, 1500);
  }

  private stopAnimation() {
    if (this.animationInterval !== null) {
      window.clearInterval(this.animationInterval);
      this.animationInterval = null;
    }
  }

  render() {
    return html`<div class="c-loading-info">
      <div class="c-loading-info__icon-container">
        <div class="c-loading-info__icon-wrapper">
          <div class="c-loading-info__icon">
            <ps-image src=${this.icon} alt="" width="64" height="64"></ps-image>
          </div>
          <svg
            class="c-loading-info__ring"
            width="140"
            height="140"
            viewBox="0 0 140 140"
            fill="none"
            aria-hidden="true"
          >
            <path
              class="c-loading-info__ring-track"
              d="M70 140 H35 A35 35 0 0 1 0 105 V35 A35 35 0 0 1 35 0 H105 A35 35 0 0 1 140 35 V105 A35 35 0 0 1 105 140 Z"
              stroke-width="2"
            />
            <path
              class="c-loading-info__ring-fill"
              d="M70 140 H35 A35 35 0 0 1 0 105 V35 A35 35 0 0 1 35 0 H105 A35 35 0 0 1 140 35 V105 A35 35 0 0 1 105 140 Z"
              stroke-width="2"
              stroke-linecap="round"
              pathLength="100"
              stroke-dasharray="100"
              stroke-dashoffset="100"
            />
          </svg>
          <div class="c-loading-info__pulse" aria-hidden="true"></div>
        </div>
      </div>

      <div
        class="c-loading-info__message-container"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <div
          class="c-loading-info__message-container-inner"
          style="transform: translateY(calc(-${this
            .currentStepIndex} * 24px + 24px));"
        >
          ${this.steps.map(
            (item) => html`<ps-text-body size="small">${item}</ps-text-body>`
          )}
        </div>
      </div>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-loading-info': LoadingInfoWC;
  }
}
